import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './SignatureServices.css';
import usePageTitle from '../hooks/usePageTitle';
import { useLanguage } from '../hooks/useLanguage';
import borderImage from '../assets/border.png';
import arrowIcon from '../assets/arrow.svg';
import goatImage from '../assets/goat.png';
import horseImage from '../assets/horse.png';
import spikeImage from '../assets/spike.png';
import passionProjectImage from '../assets/passion_project.png';
import storytellingImage from '../assets/storytelling.png';
import artExamplesImage from '../assets/art_examples.png';

const SignatureServices: React.FC = () => {
  const { t, language } = useLanguage();
  usePageTitle(t('services.title'));
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Remove animation classes first to reset
    const allElements = document.querySelectorAll('.services-title, .services-description');
    allElements.forEach(element => {
      element.classList.remove('animate-in-initial');
    });
    
    // Force reflow to ensure the removal is processed
    void document.body.offsetHeight;
    
    // Animate initial content on page load
    // Title animates first
    const titleElement = document.querySelector('.services-title');
    if (titleElement) {
      setTimeout(() => {
        titleElement.classList.add('animate-in-initial');
      }, 200);
    }
    
    // Description animates after title
    const descElement = document.querySelector('.services-description');
    if (descElement) {
      setTimeout(() => {
        descElement.classList.add('animate-in-initial');
      }, 210);
    }
  }, []);

  return (
    <div className="services-page">
      <div className="services-container">
        <h1 className="services-title">{t('services.title')}</h1>
        <img src={borderImage} alt="" className="services-decorative-line" />

        <div className="services-content">
          <p className="services-description">
            {language === 'en' ? (
              <>All signature services operate through our <em><Link to="/primus-mentorship" className="primus-link"><b>Primus Mentorship</b></Link></em>, grounded in our distinctive philosophy.</>
            ) : (
              <>모든 고유 서비스는 프리머스만의 독특한 철학에 기반한 <em><Link to="/primus-mentorship" className="primus-link"><b>프리머스 멘토십</b></Link></em>을 통해 운영됩니다.</>
            )}
          </p>
          
          <div className="services-layout">
            <img src={goatImage} alt="Goat" className="services-image-placeholder" />
            
            <div className="services-list">
              <div className={`services-item ${expandedItems.has(0) ? 'expanded' : ''}`}>
                <div className="service-header" onClick={() => toggleItem(0)}>
                  <span className="service-label">{language === 'en' ? <>I. &nbsp;&nbsp;Individualized <em>Via</em></> : <>I. &nbsp;&nbsp;{t('services.item1Title')}</>}</span>
                  <img src={arrowIcon} alt="Arrow" className={`service-arrow ${expandedItems.has(0) ? 'rotated' : ''}`} />
                </div>
                <div className={`service-content ${expandedItems.has(0) ? 'visible' : ''}`}>
                  <div className="service-content-inner">
                    <div className="service-text">
                      <p>{t('services.item1Text')}</p>
                    </div>
                    <div className="service-background horse-background">
                      <img src={horseImage} alt="Horse" className="horse-bg-image" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`services-item spike-item ${expandedItems.has(1) ? 'expanded' : ''}`}>
                <div className="service-header" onClick={() => toggleItem(1)}>
                  <span className="service-label">II. &nbsp;{t('services.item2Title')}</span>
                  <img src={arrowIcon} alt="Arrow" className={`service-arrow ${expandedItems.has(1) ? 'rotated' : ''}`} />
                </div>
                <div className={`service-content spike-content ${expandedItems.has(1) ? 'visible' : ''}`}>
                  <div className="service-content-inner spike-inner">
                    <div className="service-text spike-text">
                      <p>{t('services.item2Text')}</p>
                    </div>
                    <div className="service-background spike-background">
                      <img src={spikeImage} alt="Spike" className="spike-bg-image" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`services-item passion-item ${expandedItems.has(2) ? 'expanded' : ''}`}>
                <div className="service-header" onClick={() => toggleItem(2)}>
                  <span className="service-label">III. &nbsp;{t('services.item3Title')}</span>
                  <img src={arrowIcon} alt="Arrow" className={`service-arrow ${expandedItems.has(2) ? 'rotated' : ''}`} />
                </div>
                <div className={`service-content passion-content ${expandedItems.has(2) ? 'visible' : ''}`}>
                  <div className="service-content-inner passion-inner">
                    <div className="service-text passion-text">
                      <p>{t('services.item3Text')}</p>
                    </div>
                    <div className="service-background passion-background">
                      <img src={passionProjectImage} alt="Passion Project" className="passion-bg-image" />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`services-item storytelling-item ${expandedItems.has(3) ? 'expanded' : ''}`}>
                <div className="service-header" onClick={() => toggleItem(3)}>
                  <span className="service-label">IV. &nbsp;{t('services.item4Title')}</span>
                  <img src={arrowIcon} alt="Arrow" className={`service-arrow ${expandedItems.has(3) ? 'rotated' : ''}`} />
                </div>
                <div className={`service-content storytelling-content ${expandedItems.has(3) ? 'visible' : ''}`}>
                  <div className="service-content-inner storytelling-inner">
                    <div className="service-text storytelling-text">
                      <p>{t('services.item4Text')}</p>
                    </div>
                    <div className="service-background storytelling-background">
                      <img src={storytellingImage} alt="Storytelling" className="storytelling-bg-image" />
                    </div>
                  </div>
                </div>
              </div>

              <div className={`services-item crafts-item ${expandedItems.has(4) ? 'expanded' : ''}`}>
                <div className="service-header" onClick={() => toggleItem(4)}>
                  <span className="service-label">V. &nbsp;&nbsp;{t('services.item5Title')}</span>
                  <img src={arrowIcon} alt="Arrow" className={`service-arrow ${expandedItems.has(4) ? 'rotated' : ''}`} />
                </div>
                <div className={`service-content crafts-content ${expandedItems.has(4) ? 'visible' : ''}`}>
                  <div className="service-content-inner crafts-inner">
                    <div className="service-text crafts-text">
                      <p>{t('services.item5Text')}</p>
                    </div>
                  </div>
                  <div className="art-examples-container">
                    <img src={artExamplesImage} alt="Art Examples" className="art-examples-image" />
                    <p className="art-examples-caption"><em>{language === 'en' ? '(examples from past students)' : '(과거 학생들의 예시)'}</em></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureServices;